import { useState, useEffect } from 'react'
import { supabase } from './supabase'
import { Order, OrderItem, OrderItemOption } from './types'

export type FullOrderItem = OrderItem & {
  options: OrderItemOption[]
}

export type FullOrder = Order & {
  items: FullOrderItem[]
}

export function useOrderDetail(orderId: string | null) {
  const [order, setOrder] = useState<FullOrder | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!orderId) {
      setOrder(null)
      return
    }

    async function fetchOrder() {
      setLoading(true)
      setError(null)
      try {
        // Fetch order
        const { data: orderData, error: orderError } = await supabase
          .from('orders')
          .select('*')
          .eq('id', orderId)
          .single()

        if (orderError) throw orderError

        // Fetch items
        const { data: itemsData, error: itemsError } = await supabase
          .from('order_items')
          .select('*')
          .eq('order_id', orderId)

        console.log(
          '[ORDER-DETAIL] order_id:',
          orderId,
          'items returned:',
          itemsData?.length || 0,
          'error:',
          itemsError,
        )
        if (itemsError) throw itemsError

        // Fetch options for all items
        const itemIds = itemsData.map((item) => item.id)

        let optionsData: OrderItemOption[] = []
        if (itemIds.length > 0) {
          const { data: optData, error: optError } = await supabase
            .from('order_item_options')
            .select('*')
            .in('order_item_id', itemIds)

          if (optError) throw optError
          optionsData = optData || []
        }

        // Assemble full order
        const fullItems: FullOrderItem[] = itemsData.map((item) => ({
          ...item,
          options: optionsData.filter((opt) => opt.order_item_id === item.id),
        }))

        const fullOrder: FullOrder = {
          ...orderData,
          items: fullItems,
        }

        setOrder(fullOrder)
      } catch (err: any) {
        console.error('Error fetching order details:', err)
        setError(err)
      } finally {
        setLoading(false)
      }
    }

    fetchOrder()
  }, [orderId])

  return { order, loading, error, refetch: () => setOrder(null) }
}
